import React from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';
import { PublicSessionLoginSuccess } from 'src/assets/public-session-login-success';
import { CountdownModal } from 'src/components/modals/countdown-modal';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useCreatePublicContextSessionMutation } from 'types/graphql';
import { removePersistedValue } from 'shared/utils/persisted-values';
import { formatMaskedPhoneNumber } from '../../kiosk-pro-shared/format-phone-number';
import { PhoneNumberLoginSuccessContainer, SuccessIconContainer } from './phone-number-login-success.styles';

export function PhoneNumberLoginSuccess({
  userFirstName,
  kioskMenuUrl,
  className,
  onClose,
  onResetLoginState,
  isInModal,
}: {
  userFirstName: string;
  kioskMenuUrl: string;
  className?: string;
  onClose?: () => void;
  onResetLoginState?: () => void;
  isInModal?: boolean;
}): JSX.Element {
  const router = useRouter();
  const User = useUser();
  const { dispensary } = useDispensary();
  const kioskGuestPhoneNumber = useObserver(() => User.kioskGuestPhoneNumber);

  // Display name: show first name if logged in, otherwise show masked phone number
  const displayName = userFirstName || (kioskGuestPhoneNumber ? formatMaskedPhoneNumber(kioskGuestPhoneNumber) : '');
  const [createPublicContextSessionMutation] = useCreatePublicContextSessionMutation();
  const cName = String(dispensary?.cName ?? '');
  const dispensaryId = dispensary?.id;

  /**
   * Handles the go back action by logging out the user, resetting login state,
   * creating a new session, and navigating to the public session page with the new session ID.
   */
  const handleGoBack = async (): Promise<void> => {
    await User?.logout();
    removePersistedValue('access-token');
    onResetLoginState?.();

    if (!dispensaryId) {
      if (onClose) {
        onClose();
        return;
      }
      void router?.back();
      return;
    }

    const result = await createPublicContextSessionMutation({
      variables: {
        dispensaryId,
      },
    });

    const newSessionId = result.data?.createPublicContextSession?.id;

    if (!newSessionId) {
      if (onClose) {
        onClose();
        return;
      }
      void router?.back();
      return;
    }

    // For modal context: update session-id in URL and stay on the same page
    if (onClose) {
      void router?.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, 'session-id': newSessionId },
        },
        undefined,
        { shallow: true }
      );
      return;
    }

    // For page context: navigate to the phone login page
    void router?.push(`/kiosks/${cName}/login/public-session-phone-continue?session-id=${newSessionId}`);
  };

  return (
    <PhoneNumberLoginSuccessContainer className={className} $isInModal={isInModal}>
      <SuccessIconContainer>
        <PublicSessionLoginSuccess />
      </SuccessIconContainer>
      <CountdownModal
        startNewSession={() => void router?.push(kioskMenuUrl)}
        closeAndContinue={() => void handleGoBack()}
        timeLeftInSeconds={5}
        title={
          <>
            Shopping as
            <br />
            <b>{displayName}</b>
          </>
        }
        subtitle=''
        primaryButtonText='Looks good'
        secondaryButtonText='Go back'
      />
    </PhoneNumberLoginSuccessContainer>
  );
}
